import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CartItem } from '../types'
import { useCartStore } from '@/stores/cartStore'
import './ItemMarker.css'

interface ItemMarkerProps {
  item: CartItem
  /** Display X (frame-relative pixels) */
  displayX: number
  /** Display Y (frame-relative pixels) */
  displayY: number
  /** Display radius (scaled to current frame size) */
  displayRadius: number
  /** Item number for the flag label */
  index: number
  /** All items in current shop+section for prev/next navigation */
  allItems?: CartItem[]
  /** Callback to navigate viewer to a specific item */
  onNavigateToItem?: (item: CartItem) => void
  /** When true, hide flag labels — only show circles (dots-only mode) */
  labelsHidden?: boolean
  /** Frame display dimensions — used to clamp the detail panel inside the visible area */
  containerWidth?: number
  containerHeight?: number
}

/**
 * ItemMarker — Renders a transparent circle + flag tag on a frame.
 * Tap the flag to expand a detail card with name/price editing.
 */
export function ItemMarker({ item, displayX, displayY, displayRadius, index, allItems, onNavigateToItem, labelsHidden = false, containerWidth, containerHeight }: ItemMarkerProps) {
  const { t } = useTranslation('delivery')
  const [expanded, setExpanded] = useState(false)
  const [peeking, setPeeking] = useState(false) // Temporarily show label on circle tap in dots-only mode
  const [nameInput, setNameInput] = useState(item.name)
  const [priceInput, setPriceInput] = useState(item.price !== null ? String(item.price) : '')

  const { removeItem, addToCart, removeFromCart, updateItemName, updateItemPrice } = useCartStore()

  // Prev/next item navigation
  const { prevItem, nextItem } = useMemo(() => {
    if (!allItems || allItems.length <= 1) return { prevItem: null, nextItem: null }
    const idx = allItems.findIndex((i) => i.id === item.id)
    if (idx === -1) return { prevItem: null, nextItem: null }
    return {
      prevItem: allItems[(idx - 1 + allItems.length) % allItems.length],
      nextItem: allItems[(idx + 1) % allItems.length],
    }
  }, [allItems, item.id])

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (prevItem && onNavigateToItem) onNavigateToItem(prevItem)
  }, [prevItem, onNavigateToItem])

  const handleNext = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (nextItem && onNavigateToItem) onNavigateToItem(nextItem)
  }, [nextItem, onNavigateToItem])

  const handleFlagClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setExpanded((prev) => !prev)
  }, [])

  const handleToggleCart = useCallback((e: React.MouseEvent | React.ChangeEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Save any edits first
    if (nameInput !== item.name) updateItemName(item.id, nameInput)
    const price = priceInput ? parseFloat(priceInput) : null
    if (price !== item.price) updateItemPrice(item.id, price)
    if (item.inCart) {
      removeFromCart(item.id)
    } else {
      addToCart(item.id)
    }
  }, [item.id, item.name, item.price, item.inCart, nameInput, priceInput, addToCart, removeFromCart, updateItemName, updateItemPrice])

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    removeItem(item.id)
  }, [item.id, removeItem])

  const handleNameBlur = useCallback(() => {
    if (nameInput !== item.name) updateItemName(item.id, nameInput)
  }, [item.id, item.name, nameInput, updateItemName])

  const handlePriceBlur = useCallback(() => {
    const price = priceInput ? parseFloat(priceInput) : null
    if (price !== item.price) updateItemPrice(item.id, price)
  }, [item.id, item.price, priceInput, updateItemPrice])

  // Flag position: above the circle center
  const flagY = displayY - displayRadius

  // ── Compute clamped position for the detail panel ──────────────────────────
  // Panel dimensions (matches CSS max-width; height is an estimate)
  const PANEL_W = 220
  const PANEL_H = item.thumbnailDataUrl ? 230 : 185
  const cW = containerWidth ?? 9999
  const cH = containerHeight ?? 9999
  const MARGIN = 8

  // Vertical: prefer below the circle, flip above if it would overflow bottom
  const belowTop = displayY + displayRadius + 4
  const showAbove = belowTop + PANEL_H > cH - MARGIN
  const panelTopPx = showAbove
    ? Math.max(MARGIN, displayY - displayRadius - 4 - PANEL_H)
    : Math.min(cH - PANEL_H - MARGIN, belowTop)

  // Horizontal: center on item, clamp to keep inside frame
  const panelLeftPx = Math.max(MARGIN, Math.min(cW - PANEL_W - MARGIN, displayX - PANEL_W / 2))

  const detailPanelStyle: React.CSSProperties = {
    position: 'absolute',
    top: `${panelTopPx}px`,
    left: `${panelLeftPx}px`,
    transform: 'none',
    margin: 0,
  }

  return (
    <>
      {/* Circle overlay */}
      <div
        className={`item-marker-circle ${labelsHidden ? 'item-marker-circle-active' : ''}`}
        style={{
          left: `${displayX}px`,
          top: `${displayY}px`,
          width: `${displayRadius * 2}px`,
          height: `${displayRadius * 2}px`,
        }}
        onClick={(e) => { e.stopPropagation(); if (labelsHidden) setPeeking((p) => !p) }}
      />

      {/* Flag tag */}
      <div
        className={`item-marker-flag ${labelsHidden && !peeking ? 'item-marker-flag-hidden' : ''}`}
        style={{
          left: `${displayX}px`,
          top: `${flagY}px`,
        }}
        onClick={handleFlagClick}
      >
        <div className="item-marker-tag">
          {prevItem && (
            <button className="item-marker-nav-arrow" onClick={handlePrev} title={prevItem.name || t('marker.itemFallbackName', { number: '' })}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 01-8 0" />
          </svg>
          <span>{item.name || t('marker.itemFallbackName', { number: index })}</span>
          {nextItem && (
            <button className="item-marker-nav-arrow" onClick={handleNext} title={nextItem.name || t('marker.itemFallbackName', { number: '' })}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
        <div className="item-marker-stem" />
      </div>

      {/* Detail card — rendered as a frame-level sibling so position can be clamped */}
      {expanded && (
        <div className="item-marker-detail" style={detailPanelStyle} onClick={(e) => e.stopPropagation()}>
          {item.thumbnailDataUrl && (
            <img src={item.thumbnailDataUrl} alt="" className="item-marker-detail-thumb" />
          )}
          <input
            type="text"
            placeholder={t('marker.itemNamePlaceholder')}
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onBlur={handleNameBlur}
            onClick={(e) => e.stopPropagation()}
          />
          <input
            type="number"
            placeholder={t('marker.pricePlaceholder')}
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            onBlur={handlePriceBlur}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="item-marker-detail-actions">
            <label className="item-marker-cart-toggle" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={item.inCart}
                onChange={handleToggleCart}
              />
              <span>{item.inCart ? t('marker.inCart') : t('marker.addToCart')}</span>
            </label>
            <button className="item-marker-btn-remove" onClick={handleRemove}>
              {t('common:delete')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
